<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { navigate } from 'svelte-routing';
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';

  let promotions = [];
  let isLoading = true;
  let currentMealCategory = '';
  let localizedMealName = '';

  // Reactively calculate the dynamic placeholder based on country and meal time.
  // We use .replace(/\s+/g, '') to turn "Midnight Snack" into "MidnightSnack".
  $: dynamicPlaceholder = `/${$selectedCountry}_${currentMealCategory.replace(/\s+/g, '')}.png`;

  onMount(async () => {
      determineMealTime();
      await fetchMealDeals();
  });

  function determineMealTime() {
      const hour = new Date().getHours(); // Client's local browser hour (0-23)

      if (hour >= 6 && hour < 11) {
          currentMealCategory = 'Breakfast';
          localizedMealName = $t('mealdeals.breakfast') || 'Breakfast';
      } else if (hour >= 11 && hour < 14) {
          currentMealCategory = 'Lunch';
          localizedMealName = $t('mealdeals.lunch') || 'Lunch';
      } else if (hour >= 14 && hour < 18) {
          currentMealCategory = 'Afternoon Tea';
          localizedMealName = $t('mealdeals.afternoon_tea') || 'Afternoon Tea';
      } else if (hour >= 18 && hour < 24) {
          currentMealCategory = 'Dinner';
          localizedMealName = $t('mealdeals.dinner') || 'Dinner';
      } else {
          currentMealCategory = 'Midnight Snack';
          localizedMealName = $t('mealdeals.midnight_snack') || 'Midnight Snack';
      }
  }

  async function fetchMealDeals() {
      isLoading = true;
      try {
          const res = await fetch(getApiUrl(`/promotions/search?categories=${encodeURIComponent(currentMealCategory)}&countryCode=${$selectedCountry}&pageSize=20`));
          
          if (!res.ok) throw new Error("Failed to fetch meal deals");
          
          const result = await res.json();
          let fetchedPromos = result.data || result || [];

          // Sort by highest savings
          fetchedPromos.sort((a, b) => {
              const rewardA = a.maximumRewardPercentage || a.maximum_reward_percentage || 0;
              const rewardB = b.maximumRewardPercentage || b.maximum_reward_percentage || 0;
              
              if (rewardB !== rewardA) {
                  return rewardB - rewardA;
              }
              
              const fixedA = a.maximumReward || a.maximum_reward || 0;
              const fixedB = b.maximumReward || b.maximum_reward || 0;
              return fixedB - fixedA;
          });

          // Slice top 4 for the UI
          promotions = fetchedPromos.slice(0, 4);

      } catch (e) {
          console.error("Meal Deals Error:", e);
      } finally {
          isLoading = false;
      }
  }

  function handleViewAll(e) {
      e.preventDefault();
      
      const searchState = {
          nameQuery: '',
          selectedCategories: [currentMealCategory], // Assuming Search expects an array of strings
          selectedMethods: [],
          bankQuery: '',
          selectedCreditCards: [],
          isOnline: false,
          isInStore: false,
          spendingQuery: ''
      };
      sessionStorage.setItem('promoSearchState', JSON.stringify(searchState));
      
      navigate('/search');
  }
</script>

<div class="meal-deals-section">
  <div class="header">
      <h2>{ ($t('mealdeals.title') || 'Time for {meal}!').replace('{meal}', localizedMealName) }</h2>
      
      <a href="/search" class="view-all" on:click={handleViewAll}>
          { ($t('mealdeals.view_all') || 'View All {meal} Deals &rarr;').replace('{meal}', localizedMealName) }
      </a>
  </div>

  {#if isLoading}
      <div class="loading-skeleton">
          {#each Array(4) as _}
              <div class="skeleton-card"></div>
          {/each}
      </div>
  {:else if promotions.length === 0}
      <div class="no-deals">
          { ($t('mealdeals.no_deals') || 'No specific deals at the moment.').replace('{meal}', localizedMealName) }
      </div>
  {:else}
      <div class="deals-grid">
          {#each promotions as promo}
              <a href={`/promotions/detail/${promo._id || promo.id}`} class="deal-card card-shadow" on:click|preventDefault={() => navigate(`/promotions/detail/${promo._id || promo.id}`)}>
                  <div class="image-container">
                      <img 
                          src={Array.isArray(promo.images) && promo.images.length > 0 ? promo.images[0] : (promo.imageUrl || dynamicPlaceholder)} 
                          alt={promo.name} 
                          on:error={(e) => {
                              // Safely fallback to the dynamic placeholder, preventing infinite loops if it's missing
                              if (!e.target.src.includes(dynamicPlaceholder)) {
                                  e.target.src = dynamicPlaceholder;
                              }
                          }}
                      />
                      
                      <!-- Highlight Badge for Savings -->
                      {#if promo.maximumRewardPercentage || promo.maximum_reward_percentage}
                          <div class="savings-badge">
                              { ($t('mealdeals.up_to_percent') || 'Up to {reward}% Off').replace('{reward}', promo.maximumRewardPercentage || promo.maximum_reward_percentage) }
                          </div>
                      {:else if promo.maximumReward || promo.maximum_reward}
                          <div class="savings-badge">
                              { ($t('mealdeals.save_up_to') || 'Save up to ${reward}').replace('{reward}', promo.maximumReward || promo.maximum_reward) }
                          </div>
                      {/if}
                  </div>
                  
                  <div class="deal-info">
                      <h3 class="deal-name">{promo.name}</h3>
                  </div>
              </a>
          {/each}
      </div>
  {/if}
</div>

<style>
  .meal-deals-section {
      margin: 3rem 0;
      padding: 0 1rem;
  }

  .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-bottom: 1.5rem;
      flex-wrap: wrap;
      gap: 1rem;
  }

  h2 {
      margin: 0;
      font-size: 1.8rem;
      font-weight: 800;
      color: #333;
  }

  .view-all {
      color: #007bff;
      font-weight: 600;
      font-size: 1rem;
      transition: color 0.2s;
      text-decoration: none;
      cursor: pointer;
  }

  .view-all:hover {
      color: #0056b3;
      text-decoration: underline;
  }

  /* 4 in a row Desktop, 2 Tablet, 1 Mobile */
  .deals-grid {
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      gap: 1.5rem;
  }

  @media (min-width: 600px) {
      .deals-grid {
          grid-template-columns: repeat(2, 1fr);
      }
  }

  @media (min-width: 992px) {
      .deals-grid {
          grid-template-columns: repeat(4, 1fr);
      }
  }

  .deal-card {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border-radius: 12px;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      background: white;
      text-decoration: none;
      color: inherit;
  }

  .deal-card:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0,0,0,0.1);
  }

  .image-container {
      position: relative;
      width: 100%;
      aspect-ratio: 4/3;
      background: #f4f4f4;
      overflow: hidden;
  }

  .image-container img {
      width: 100%;
      height: 100%;
      object-fit: cover;
  }

  .savings-badge {
      position: absolute;
      top: 10px;
      left: 10px;
      background: #dc3545; /* Red highlight for deals */
      color: white;
      padding: 5px 10px;
      border-radius: 4px;
      font-size: 0.85rem;
      font-weight: bold;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }

  .deal-info {
      padding: 1rem;
  }

  .deal-name {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: #222;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      line-height: 1.3;
  }

  .no-deals {
      text-align: center;
      padding: 3rem;
      background: white;
      border-radius: 8px;
      color: #666;
      font-style: italic;
  }

  /* Skeleton Loading */
  .loading-skeleton {
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      gap: 1.5rem;
  }

  @media (min-width: 600px) { .loading-skeleton { grid-template-columns: repeat(2, 1fr); } }
  @media (min-width: 992px) { .loading-skeleton { grid-template-columns: repeat(4, 1fr); } }

  .skeleton-card {
      height: 250px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
      border-radius: 12px;
  }

  @keyframes loading {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
  }
</style>